import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Select, Modal, message, Tooltip, Table, Tag } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBoxOpen,
  faTruckFast,
  faVial,
  faFlaskVial,
  faRotate,
  faPaperPlane,
} from '@fortawesome/free-solid-svg-icons';
import { usePageHeader } from '../../../layouts/AdminLayout';
import OnDemandOrdersService from '../../../services/OnDemandOrdersService';
import {
  PageContainer,
  TopBar,
  ActionButton,
  StyledTable,
  StatusTag,
  EmptyState,
  StatsRow,
  StatCard,
} from '../StorageUnits/styled';

// Payment/fulfilment status of the storefront order itself.
const ORDER_STATUS_META = {
  pending: { label: 'Pending', color: 'default' },
  paid: { label: 'Paid', color: 'blue' },
  processing: { label: 'Processing', color: 'geekblue' },
  shipped: { label: 'Shipped', color: 'cyan' },
  completed: { label: 'Completed', color: 'green' },
  cancelled: { label: 'Cancelled', color: 'red' },
};

// Endless Health kit status.
const KIT_STATUS_META = {
  ORDER_CREATED: { label: 'Kit ordered', color: 'blue' },
  LINKED: { label: 'Linked', color: 'blue' },
  SHIPPED: { label: 'Shipped', color: 'cyan' },
  DELIVERED: { label: 'Delivered', color: 'cyan' },
  KIT_ACTIVATED: { label: 'Activated', color: 'geekblue' },
  LAB_PROCESSING: { label: 'Lab processing', color: 'purple' },
  RESULTS_READY: { label: 'Results ready', color: 'green' },
  SAMPLE_REJECTED: { label: 'Sample rejected', color: 'red' },
  KIT_CANCELED: { label: 'Canceled', color: 'red' },
  ERROR: { label: 'Error', color: 'red' },
};

const STATUS_FILTERS = [
  { value: 'all', label: 'All statuses' },
  { value: 'paid', label: 'Paid (awaiting kit)' },
  { value: 'processing', label: 'Processing' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const fullName = (c) => (c ? `${c.firstName || ''} ${c.lastName || ''}`.trim() : '—');
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');

const OrdersPage = () => {
  const { setPageHeader } = usePageHeader();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [actioningId, setActioningId] = useState(null);
  const [resultsOrder, setResultsOrder] = useState(null);

  useEffect(() => {
    setPageHeader({ title: 'Orders', count: orders.length, loading });
  }, [setPageHeader, orders.length, loading]);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const response = await OnDemandOrdersService.getOrders({
        status: statusFilter === 'all' ? undefined : statusFilter,
      });
      if (response.success) {
        setOrders(response.data || []);
      } else if (response.message) {
        message.error(response.message);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      message.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const stats = useMemo(() => {
    const acc = { awaitingKit: 0, inProgress: 0, resultsReady: 0, total: orders.length };
    for (const o of orders) {
      const hasKit = Boolean(o.endless?.orderId);
      const hasResults = Boolean(o.endless?.results?.length);
      if (!hasKit && o.status === 'paid') acc.awaitingKit += 1;
      else if (hasKit && !hasResults) acc.inProgress += 1;
      if (hasResults) acc.resultsReady += 1;
    }
    return acc;
  }, [orders]);

  const handleCreateKit = (record) => {
    Modal.confirm({
      title: 'Create kit order & ship?',
      content: `This registers the order with Endless Health and ships a kit to ${fullName(
        record.customer
      )} at ${record.customer?.address?.line1 || 'their address'}.`,
      okText: 'Create & ship kit',
      onOk: async () => {
        setActioningId(record._id);
        try {
          const response = await OnDemandOrdersService.createKit(record._id);
          if (response.success) {
            message.success('Kit order created — shipping to customer');
            fetchOrders();
          } else {
            message.error(response.message || 'Failed to create kit order');
          }
        } catch (error) {
          message.error(error.response?.data?.message || 'Failed to create kit order');
        } finally {
          setActioningId(null);
        }
      },
    });
  };

  const handleSync = async (record) => {
    setActioningId(record._id);
    try {
      const response = await OnDemandOrdersService.syncOrder(record._id);
      if (response.success) {
        const ready = response.data?.endless?.results?.length;
        message.success(ready ? 'Results updated' : 'Status refreshed');
        fetchOrders();
      } else {
        message.error(response.message || 'Failed to sync order');
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to sync order');
    } finally {
      setActioningId(null);
    }
  };

  const handleRegisterWebhook = async (replace = false) => {
    try {
      const response = await OnDemandOrdersService.registerWebhook({ replace });
      if (response.success) {
        message.success('Webhook registered with Endless Health');
      } else if (response.code === 'WEBHOOK_EXISTS' && !replace) {
        Modal.confirm({
          title: 'Replace existing Endless webhook?',
          content:
            'Endless allows one active webhook. Replacing it points all kit updates to this server and issues a new signing secret.',
          okText: 'Replace',
          onOk: () => handleRegisterWebhook(true),
        });
      } else {
        message.error(response.message || 'Failed to register webhook');
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to register webhook');
    }
  };

  const columns = [
    {
      title: 'Order',
      key: 'order',
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 600 }}>{r.orderNumber}</div>
          <div style={{ fontSize: 12, color: '#999' }}>{fmtDate(r.createdAt)}</div>
        </div>
      ),
    },
    {
      title: 'Customer',
      key: 'customer',
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 600 }}>{fullName(r.customer)}</div>
          <div style={{ fontSize: 12, color: '#999' }}>{r.customer?.email}</div>
        </div>
      ),
    },
    {
      title: 'Items',
      key: 'items',
      render: (_, r) =>
        (r.items || []).map((i) => i.panelName || i.name).join(', ') || '—',
    },
    {
      title: 'Payment',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const meta = ORDER_STATUS_META[status] || { label: status, color: 'default' };
        return <StatusTag color={meta.color}>{meta.label}</StatusTag>;
      },
    },
    {
      title: 'Kit status',
      key: 'kitStatus',
      render: (_, r) => {
        const s = r.endless?.status;
        if (!s) return <Tag>Not created</Tag>;
        const meta = KIT_STATUS_META[s] || { label: s, color: 'default' };
        return (
          <Tooltip title={r.endless?.orderId ? `Endless #${r.endless.orderId}` : undefined}>
            <StatusTag color={meta.color}>{meta.label}</StatusTag>
          </Tooltip>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 260,
      render: (_, record) => {
        const busy = actioningId === record._id;
        const hasKit = Boolean(record.endless?.orderId);
        const hasResults = Boolean(record.endless?.results?.length);

        if (!hasKit) {
          const canShip = record.status === 'paid' || record.status === 'processing';
          const btn = (
            <ActionButton
              type="primary"
              size="small"
              loading={busy}
              disabled={!canShip}
              onClick={() => handleCreateKit(record)}
            >
              Create kit &amp; ship
            </ActionButton>
          );
          return canShip ? (
            btn
          ) : (
            <Tooltip title="Order must be paid before a kit can be shipped">
              <span>{btn}</span>
            </Tooltip>
          );
        }

        return (
          <div style={{ display: 'flex', gap: 8 }}>
            {hasResults && (
              <ActionButton size="small" onClick={() => setResultsOrder(record)}>
                View results
              </ActionButton>
            )}
            <ActionButton size="small" loading={busy} onClick={() => handleSync(record)}>
              <FontAwesomeIcon icon={faRotate} style={{ marginRight: 6 }} />
              Sync
            </ActionButton>
          </div>
        );
      },
    },
  ];

  return (
    <PageContainer>
      <StatsRow className="header-animate-in">
        <StatCard $bgColor="#E6F4FF" $iconColor="#1890FF">
          <div className="stat-icon">
            <FontAwesomeIcon icon={faBoxOpen} />
          </div>
          <div className="stat-info">
            <div className="stat-value">{stats.awaitingKit}</div>
            <div className="stat-label">Awaiting kit</div>
          </div>
        </StatCard>
        <StatCard $bgColor="#F9F0FF" $iconColor="#722ED1">
          <div className="stat-icon">
            <FontAwesomeIcon icon={faTruckFast} />
          </div>
          <div className="stat-info">
            <div className="stat-value">{stats.inProgress}</div>
            <div className="stat-label">In progress</div>
          </div>
        </StatCard>
        <StatCard $bgColor="#F6FFED" $iconColor="#28A745">
          <div className="stat-icon">
            <FontAwesomeIcon icon={faFlaskVial} />
          </div>
          <div className="stat-info">
            <div className="stat-value">{stats.resultsReady}</div>
            <div className="stat-label">Results ready</div>
          </div>
        </StatCard>
        <StatCard $bgColor="#FFF7E6" $iconColor="#FA8C16">
          <div className="stat-icon">
            <FontAwesomeIcon icon={faVial} />
          </div>
          <div className="stat-info">
            <div className="stat-value">{stats.total}</div>
            <div className="stat-label">Total orders</div>
          </div>
        </StatCard>
      </StatsRow>

      <TopBar>
        <Select
          value={statusFilter}
          onChange={setStatusFilter}
          options={STATUS_FILTERS}
          style={{ width: 220 }}
        />
        <div style={{ display: 'flex', gap: 8 }}>
          <Tooltip title="Register our result webhook with Endless Health (one-time setup)">
            <ActionButton onClick={() => handleRegisterWebhook()}>
              <FontAwesomeIcon icon={faPaperPlane} style={{ marginRight: 6 }} />
              Webhook setup
            </ActionButton>
          </Tooltip>
          <ActionButton onClick={fetchOrders}>Refresh</ActionButton>
        </div>
      </TopBar>

      <StyledTable
        className="table-animate-in"
        rowKey="_id"
        columns={columns}
        dataSource={orders}
        loading={loading}
        scroll={{ x: 1000 }}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        locale={{
          emptyText: (
            <EmptyState>
              <div className="icon">
                <FontAwesomeIcon icon={faBoxOpen} />
              </div>
              <div className="title">No orders</div>
              <div className="description">
                Orders appear here after customers purchase a test kit.
              </div>
            </EmptyState>
          ),
        }}
      />

      <Modal
        open={Boolean(resultsOrder)}
        title={
          resultsOrder
            ? `Results — ${resultsOrder.orderNumber} (${fullName(resultsOrder.customer)})`
            : 'Results'
        }
        onCancel={() => setResultsOrder(null)}
        footer={null}
        width={640}
      >
        {resultsOrder && (
          <>
            <div style={{ marginBottom: 12, color: '#666' }}>
              Reported {fmtDate(resultsOrder.endless?.resultsReadyAt)} · Endless #
              {resultsOrder.endless?.orderId}
            </div>
            <Table
              rowKey={(r) => r.biomarker}
              size="small"
              pagination={false}
              dataSource={resultsOrder.endless?.results || []}
              columns={[
                { title: 'Biomarker', dataIndex: 'biomarker', key: 'biomarker' },
                { title: 'Value', dataIndex: 'value', key: 'value' },
                { title: 'Units', dataIndex: 'units', key: 'units' },
              ]}
            />
          </>
        )}
      </Modal>
    </PageContainer>
  );
};

export default OrdersPage;
